import { ReactElement } from 'react';

import { create } from 'zustand';
import { UploadFile } from 'antd';

import {
  LibraryStoryDetailResponse,
  LibraryStoryListResponse,
  LibraryStoryListPaginateResponse,
  TagLibrary,
} from 'routes/Employer/LibraryV2/LibraryV2.interface';
import { GetAdvocatesByCompanyIdPayload } from 'types/store/GetAdvocatesByCompanyId';
import { PreviewUrlProps } from 'components/module/PreviewUrl/PreviewUrl.props';
import { ShareableSmartGroup } from 'types/SmartGroup';

type LibraryV2State = {
  isLoadingList: boolean;
  isLoadingDetail: boolean;
  listStories: LibraryStoryListResponse[];
  storyDetail: LibraryStoryDetailResponse;
  currentPagination: LibraryStoryListPaginateResponse;
  isEditing: boolean;
  isViewDetail: boolean;
  storyEditing: LibraryStoryDetailResponse | null;
  storySelected: LibraryStoryListResponse;
  listTags: TagLibrary[];
  listAdvsByCompanyId: GetAdvocatesByCompanyIdPayload[];
  isPreviewImg: boolean;
  imagePreviewLink: string;
  isDeleteStory: boolean;
  isMarked: boolean;
  isFilterChanged: boolean;
  contentDetail: any;
  sizeLayout: number[];
  dataConfig: any;
  // preview
  unfurlPreview: ReactElement | null;
  unfurlPreviewRawData: PreviewUrlProps | null;
  thumbnailFileList: UploadFile[];
  listSmartGroups: ShareableSmartGroup[];
};

type LibraryV2Actions = {
  setIsLoading: (value: boolean) => void;
  setIsLoadingDetail: (value: boolean) => void;
  setListStories: (value: LibraryStoryListResponse[]) => void;
  refetchListStories: (value: LibraryStoryListResponse[]) => void;
  setStoryDetail: (value: LibraryStoryDetailResponse) => void;
  setCurrentPagination: (value: LibraryStoryListPaginateResponse) => void;
  setStorySelectedEdit: (value: LibraryStoryDetailResponse | any) => void;
  clearStorySelectedEdit: () => void;
  setStorySelected: (value: LibraryStoryListResponse) => void;
  setIsEditing: (value: boolean) => void;
  setIsViewDetail: (value: boolean) => void;
  setListTags: (value: TagLibrary[]) => void;
  setIsPreviewImg: (value: boolean) => void;
  setImagePreviewLink: (value: string) => void;
  setIsMarked: (value: boolean) => void;
  setFilterChanged: (value: boolean) => void;
  setListAdvsByCompanyId: (value: GetAdvocatesByCompanyIdPayload[]) => void;
  setIsDeleteStory: (value: boolean) => void;
  setContentDetail: (value: any) => void;
  setSizeLayout: (value: number[]) => void;
  setDataConfig: (value: any) => void;
  setUnfurlPreview: (value: ReactElement | null) => void;
  setUnfurlPreviewRawData: (value: any) => void;
  setThumbnailFileList: (value: UploadFile[]) => void;
  setListSmartGroups: (value: ShareableSmartGroup[]) => void;
  reset: () => void;
};

const initialState = {};

export const useLibraryV2 = create<LibraryV2State & LibraryV2Actions>(
  (set, get) => ({
    isLoadingList: false,
    isLoadingDetail: false,
    isEditing: false,
    isViewDetail: false,
    listStories: [],
    storyDetail: {} as LibraryStoryDetailResponse,
    currentPagination: {} as LibraryStoryListPaginateResponse,
    listTags: [],
    listAdvsByCompanyId: [],
    storyEditing: {} as LibraryStoryDetailResponse,
    storySelected: {} as LibraryStoryListResponse,
    isPreviewImg: false,
    imagePreviewLink: '',
    isDeleteStory: false,
    isMarked: false,
    isFilterChanged: false,
    contentDetail: {},
    sizeLayout: [],
    dataConfig: {},
    unfurlPreview: null,
    unfurlPreviewRawData: null,
    thumbnailFileList: [],
    listSmartGroups: [],
    setListSmartGroups: (listSmartGroups: ShareableSmartGroup[]) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        listSmartGroups,
      });
    },
    reset: () => {
      const oldData = { ...get() };
      set({ ...oldData, ...initialState });
    },
    setListAdvsByCompanyId: (
      listAdvsByCompanyId: GetAdvocatesByCompanyIdPayload[],
    ) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        listAdvsByCompanyId,
      });
    },
    setListTags: (listTags: TagLibrary[]) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        listTags,
      });
    },
    setIsPreviewImg: (isPreviewImg: boolean) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        isPreviewImg,
      });
    },
    setIsMarked: (isMarked: boolean) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        isMarked,
      });
    },
    setFilterChanged: (isFilterChanged: boolean) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        isFilterChanged,
      });
    },
    setContentDetail: (contentDetail: any) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        contentDetail,
      });
    },
    setImagePreviewLink: (imagePreviewLink: string) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        imagePreviewLink,
      });
    },
    setStorySelectedEdit: (storyEditing: LibraryStoryDetailResponse) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        storyEditing,
      });
    },
    clearStorySelectedEdit: () => {
      const oldData = { ...get() };
      set({
        ...oldData,
        storyEditing: null,
      });
    },
    setStorySelected: (storySelected: LibraryStoryListResponse) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        storySelected,
      });
    },
    setIsEditing: (isEditing: boolean) => {
      const oldData = { ...get() };
      set({ ...oldData, isEditing });
    },
    setIsDeleteStory: (isDeleteStory: boolean) => {
      const oldData = { ...get() };
      set({ ...oldData, isDeleteStory });
    },
    setIsViewDetail: (isViewDetail: boolean) => {
      const oldData = { ...get() };
      set({ ...oldData, isViewDetail });
    },
    setIsLoading: (isLoadingList: boolean) => {
      const oldData = { ...get() };
      set({ ...oldData, isLoadingList });
    },
    setIsLoadingDetail: (isLoadingDetail: boolean) => {
      const oldData = { ...get() };
      set({ ...oldData, isLoadingDetail });
    },
    setListStories: (listStoriesNew: LibraryStoryListResponse[]) => {
      const oldData = { ...get() };
      const previousListStories = oldData.listStories;
      set({
        ...oldData,
        listStories: previousListStories.concat(listStoriesNew),
      });
    },
    refetchListStories: (listStories: LibraryStoryListResponse[]) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        listStories,
      });
    },
    setStoryDetail: (storyDetail: LibraryStoryDetailResponse) => {
      const oldData = { ...get() };
      set({ ...oldData, storyDetail });
    },
    setCurrentPagination: (
      currentPagination: LibraryStoryListPaginateResponse,
    ) => {
      const oldData = { ...get() };
      set({ ...oldData, currentPagination });
    },
    setSizeLayout: (sizeLayout: number[]) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        sizeLayout,
      });
    },
    setDataConfig: (dataConfig: any) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        dataConfig,
      });
    },
    setUnfurlPreview: (unfurlPreview: ReactElement | null) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        unfurlPreview,
      });
    },
    setUnfurlPreviewRawData: (unfurlPreviewRawData: any) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        unfurlPreviewRawData,
      });
    },
    setThumbnailFileList: (thumbnailFileList: UploadFile[]) => {
      const oldData = { ...get() };
      set({
        ...oldData,
        thumbnailFileList,
      });
    },
  }),
);
